/**
 * Sprint 20 M5 (parity) — CSA crisis dataset, DC-EXT-SIGNAL-v1 compliant.
 * Rewritten from the Sprint 4 stub to the locked data model from the
 * parity-review outcome spec §3.  Supersedes the old Certainty enum
 * ('high'/'medium'/'low') with the canonical Trust-A mapping:
 *   Likely → 68%, Possible → 31%, Unlikely → 6%
 */
import type { GroundedReco } from '../../copilot-rail/reco';
import type { BoardSignal } from './occupancy-data';

export type Certainty = 'Likely' | 'Possible' | 'Unlikely';

/** Trust-A certainty → integer probability (DC-EXT-SIGNAL-v1). */
export const CERTAINTY_TO_PROBABILITY: Record<Certainty, number> = {
  Likely: 68,
  Possible: 31,
  Unlikely: 6,
};

export function certaintyToProbability(c: Certainty): number {
  return CERTAINTY_TO_PROBABILITY[c];
}

export interface ExternalSignal {
  id: string;
  source: 'MeteoSwiss' | 'BAG/FOPH' | 'Alertswiss/BABS' | 'SED-ETH' | 'Microsoft Web IQ';
  feed: string;
  status: string;
  trustClass: 'Trust-A' | 'Trust-B';
  lageLevel?: string;
  certainty: Certainty;
  probability: number;    // derived via CERTAINTY_TO_PROBABILITY
  feedsLever?: string;
  licence: string;
  provenance: string;
  filtered?: boolean;     // Test/quarantined signals RENDER but do NOT arm a lever
  webCitations?: { title: string; uri: string; publishedAt?: string; snippet?: string }[];
}

export interface Scenario {
  id: string;
  name: string;
  bedImpact: number;
  isSpof: boolean;
  probability: number;
  triggerSignal?: string;
}

export interface ScenarioRun {
  id: string;
  scenarioId: string;
  params: Record<string, unknown>;
  status: 'draft' | 'running' | 'complete';
  result?: Record<string, unknown>;
}

/** Scenario-queue result badge (Sprint 27 — middle lane). */
export type QueueResult = 'SIMULATE' | 'MODELLED' | 'WATCH' | 'HOLDS' | 'STRESS-MAX';

/** A pressure-tested shock in the scenario queue. */
export interface QueuedScenario {
  id: string;            // 'SC-01'
  name: string;          // 'Oncology RN sick-call'
  trigger: string;       // 'binding constraint fails'
  impact: string;        // '−2 oncology beds'
  impactTone: 'over' | 'watch' | 'ok' | 'muted';
  likelihood: string;    // 'Likely'
  result: QueueResult;
  recoId: string;        // route target when the row is clicked
}

/** Leading glyph per resilience lever (Sprint 27 — lower lane). */
export type ResilienceLeverIcon = 'spof' | 'buffer' | 'discharge' | 'gate' | 'escalation';

export interface ResilienceLever {
  id: string;
  label: string;
  description: string;
  bedsProtected: number;   // beds the lever protects (drives the bar)
  impactLabel?: string;    // overrides the "N beds" label, e.g. 'last'
  icon: ResilienceLeverIcon;
  owner: string;
  timing: string;          // e.g. 'likely', 'possible', 'ready', 'worst-case'
  timingTone: 'over' | 'watch' | 'ok' | 'muted';
  window: string;          // right-side action, e.g. 'pre-stage', 'arm', 'gate'
  spof?: boolean;          // 'THE SPOF' badge
  handoffTo?: string;      // cross-agent handoff, e.g. 'sba reserve', 'exec'
  recoId: string;
}

/** Header roll-up for the resilience levers board (display). */
export interface ResilienceSummary {
  stressTested: number;  // shocks stress-tested
  holdsUnder: number;    // plan holds under N
  needsReserve: number;  // N need the reserve pulled
}

/** Footer roll-up: how many shocks are absorbed as levers pre-stage. */
export interface AbsorbedSummary {
  absorbed: number;      // 5
  total: number;         // 6
}

export interface CrisisPayload {
  residualBeds: number;   // carried in from sba (0 = balanced steady state)
  signals: ExternalSignal[];
  boardSignals: BoardSignal[];
  scenarios: Scenario[];
  scenarioQueue: QueuedScenario[];
  resilienceLevers: ResilienceLever[];
  resilienceSummary: ResilienceSummary;
  absorbed: AbsorbedSummary;
  recoById: Record<string, GroundedReco>;
  defaultReco: GroundedReco;
}

const AGENT_LABEL = 'Crisis Copilot';
const CITES = ['gold.crisis_signals', 'gold.fact_capacity_baseline'];

const recoById: Record<string, GroundedReco> = {
  'heatwave-surge': {
    agentLabel: AGENT_LABEL,
    contextChip: {
      subject: 'Heatwave demand surge — simulate',
      qualifiers: ['68% probability', 'SPOF', '+14 beds'],
      status: 'Likely',
      tone: 'watch',
    },
    read:
      'MeteoSwiss Trust-A level-3 heatwave warning is active at 68% probability. ' +
      'Simulating this scenario projects a 14-bed surge across medical and ICU wards. ' +
      'Running the simulation requires approved-to-apply before csa-agent fires the csa-simulate notebook.',
    levers: [
      {
        text: 'Trigger csa-simulate notebook for heatwave-surge scenario',
        impact: { label: '14 beds at risk', tone: 'beds' },
      },
      {
        text: 'Pre-position surge beds and staffing via sba-agent handoff',
        impact: { label: 'Risk mitigation', tone: 'trust' },
      },
      {
        text: 'Notify incident commander and site medical director',
        impact: { label: '68% probability', tone: 'probability' },
      },
    ],
    primaryCta: {
      label: 'Run heatwave simulation',
      kind: 'action',
      requiresApproval: true,
    },
    projection: '+14 beds in 72h if heatwave materialises',
    citations: CITES,
    provenance: 'simulated',
  },
  'resp-virus-surge': {
    agentLabel: AGENT_LABEL,
    contextChip: {
      subject: 'Respiratory virus surge — simulate',
      qualifiers: ['31% probability', '+9 beds'],
      status: 'Possible',
      tone: 'watch',
    },
    read:
      'BAG/FOPH Trust-A Sentinella signal is Possible (31%). ' +
      'Simulating projects a 9-bed surge primarily across respiratory and ICU wards.',
    levers: [
      {
        text: 'Trigger csa-simulate notebook for resp-virus-surge scenario',
        impact: { label: '9 beds at risk', tone: 'beds' },
      },
      {
        text: 'Review respiratory isolation ward capacity',
        impact: { label: '31% probability', tone: 'probability' },
      },
    ],
    primaryCta: {
      label: 'Run respiratory virus simulation',
      kind: 'action',
      requiresApproval: true,
    },
    projection: '+9 beds in 72h if respiratory surge materialises',
    citations: CITES,
    provenance: 'simulated',
  },
  'seismic-event': {
    agentLabel: AGENT_LABEL,
    contextChip: {
      subject: 'Seismic event response — simulate',
      qualifiers: ['6% probability', 'SPOF', '+5 beds'],
      status: 'Unlikely',
      tone: 'pending',
    },
    read:
      'SED-ETH Trust-A seismic signal is Unlikely (6%). ' +
      'Low probability but a SPOF event would require activating full emergency protocols.',
    levers: [
      {
        text: 'Trigger csa-simulate notebook for seismic-event scenario',
        impact: { label: '5 beds at risk', tone: 'beds' },
      },
      {
        text: 'Verify structural safety protocols and SPOF mitigations',
        impact: { label: 'SPOF mitigation', tone: 'trust' },
      },
    ],
    primaryCta: {
      label: 'Run seismic event simulation',
      kind: 'action',
      requiresApproval: true,
    },
    projection: '+5 beds if seismic event materialises',
    citations: CITES,
    provenance: 'simulated',
  },
  'crisis-readiness': {
    agentLabel: AGENT_LABEL,
    contextChip: {
      subject: 'Site crisis readiness',
      qualifiers: ['3 active signals', '0 residual beds'],
      status: 'OK',
      tone: 'ok',
    },
    read:
      'Site is at balanced steady-state (0 residual beds from sba). ' +
      '3 Trust-A signals are active; 1 Alertswiss test signal is quarantined by the DC-EXT-SIGNAL-v1 gate. ' +
      'Top risk: heatwave-surge at 68%. No immediate crisis posture change required.',
    levers: [
      {
        text: 'Monitor heatwave-surge signal — highest probability scenario',
        impact: { label: '68% probability', tone: 'probability' },
      },
      {
        text: 'Verify surge plan covers both heatwave and resp-virus scenarios',
        impact: { label: 'Dual scenario', tone: 'trust' },
      },
    ],
    primaryCta: {
      label: 'Review full crisis readiness',
      kind: 'action',
    },
    projection: '0 residual beds; site balanced — proactive readiness only',
    citations: CITES,
    provenance: 'simulated',
  },
  // Refused reco for the quarantined Alertswiss test signal (DC-EXT-SIGNAL-v1 gate)
  'alertswiss-heat-test': {
    agentLabel: AGENT_LABEL,
    contextChip: {
      subject: 'Alertswiss test signal — quarantined',
      qualifiers: ['filtered', 'DC-EXT-SIGNAL-v1'],
      status: 'Filtered',
      tone: 'signal',
    },
    read:
      'This Alertswiss/BABS signal was flagged as a test/quarantined payload by the ' +
      'DC-EXT-SIGNAL-v1 gate (filtered=true). It renders for visibility but does NOT arm any ' +
      'crisis lever. No simulation action can be triggered for quarantined signals.',
    levers: [],
    primaryCta: {
      label: 'Quarantined — no action available',
      kind: 'action',
    },
    projection: 'Signal quarantined; lever not armed per DC-EXT-SIGNAL-v1',
    citations: ['dc-ext-signal-v1.gate'],
    provenance: 'simulated',
    refused: true,
  },
};

const defaultReco: GroundedReco = {
  agentLabel: AGENT_LABEL,
  contextChip: {
    subject: 'Crisis readiness summary',
    qualifiers: ['0 residual beds', '3 active signals'],
    tone: 'ok',
  },
  read:
    'Site is at balanced steady-state. Three Trust-A signals are active; ' +
    'the heatwave-surge scenario at 68% is the primary risk. ' +
    'Simulate the top scenario to validate surge capacity.',
  levers: [
    {
      text: 'Run heatwave-surge simulation (highest probability, 68%)',
      impact: { label: '68% probability', tone: 'probability' },
    },
    {
      text: 'Monitor BAG/FOPH respiratory signal (Possible, 31%)',
      impact: { label: '31% probability', tone: 'probability' },
    },
  ],
  primaryCta: {
    label: 'Review crisis readiness',
    kind: 'action',
  },
  projection: '0 residual beds; proactive readiness',
  citations: CITES,
  provenance: 'simulated',
};

/** Stable sort: probability desc, tie-break by id (mirrors sortReslotLevers in or-steering-data). */
export function sortScenarios(scenarios: Scenario[]): Scenario[] {
  return [...scenarios].sort((a, b) => b.probability - a.probability || a.id.localeCompare(b.id));
}

export const CRISIS_PINNED: CrisisPayload = {
  residualBeds: 0,   // MUST be 0: golden-thread sba→csa steady-state (sba closes the gap)
  signals: [
    {
      id: 'meteoswiss-heat',
      source: 'MeteoSwiss',
      feed: 'CAP-CH Hitzewelle Warnung Level 3',
      status: 'ACTIVE',
      trustClass: 'Trust-A',
      lageLevel: 'Orange',
      certainty: 'Likely',
      probability: certaintyToProbability('Likely'),
      feedsLever: 'heatwave-surge',
      licence: 'MeteoSwiss Open Data (OGD) — CC BY 4.0',
      provenance: 'simulated',
    },
    {
      id: 'bag-resp',
      source: 'BAG/FOPH',
      feed: 'Sentinella Respiratory Virus Surveillance',
      status: 'ELEVATED',
      trustClass: 'Trust-A',
      certainty: 'Possible',
      probability: certaintyToProbability('Possible'),
      feedsLever: 'resp-virus-surge',
      licence: 'BAG/FOPH Open Government Data — dl-ch/2.0',
      provenance: 'simulated',
    },
    {
      id: 'sed-seismic',
      source: 'SED-ETH',
      feed: 'SED SeismoStats Minor Seismic Activity',
      status: 'MONITORING',
      trustClass: 'Trust-A',
      lageLevel: 'Yellow',
      certainty: 'Unlikely',
      probability: certaintyToProbability('Unlikely'),
      feedsLever: 'seismic-event',
      licence: 'SED Open Data — CC BY 4.0',
      provenance: 'simulated',
    },
    {
      id: 'alertswiss-heat-test',
      source: 'Alertswiss/BABS',
      feed: 'TEST Alertswiss CAP Hitzewelle',
      status: 'TEST',
      trustClass: 'Trust-A',
      certainty: 'Likely',
      probability: certaintyToProbability('Likely'),
      licence: 'Alertswiss/BABS Public Alerts — CC BY 4.0',
      provenance: 'simulated',
      filtered: true,   // DC-EXT-SIGNAL-v1: quarantined — renders but does NOT arm a lever
    },
    {
      id: 'webiq-outbreak',
      source: 'Microsoft Web IQ',
      feed: 'Web/news early-warning (Trust-B, advisory)',
      status: 'WATCH',
      trustClass: 'Trust-B',
      certainty: 'Possible',
      probability: certaintyToProbability('Possible'),
      licence: 'microsoft-web-iq-preview-terms',
      provenance: 'simulated',
      filtered: true,   // Trust-B: renders but does NOT arm a lever (ADR-0036, ADR-0060)
      webCitations: [
        {
          title: 'Regional respiratory-illness uptick reported',
          uri: 'https://example.invalid/webiq/news/respiratory-uptick',
          publishedAt: '2026-08-12T06:00:00Z',
        },
      ],
    },
  ],
  scenarios: [
    {
      id: 'heatwave-surge',
      name: 'Summer heatwave demand surge',
      bedImpact: 14,
      isSpof: true,
      probability: certaintyToProbability('Likely'),   // 68
      triggerSignal: 'meteoswiss-heat',
    },
    {
      id: 'resp-virus-surge',
      name: 'Respiratory virus surge',
      bedImpact: 9,
      isSpof: false,
      probability: certaintyToProbability('Possible'), // 31
      triggerSignal: 'bag-resp',
    },
    {
      id: 'seismic-event',
      name: 'Seismic event response',
      bedImpact: 5,
      isSpof: true,
      probability: certaintyToProbability('Unlikely'), // 6
      triggerSignal: 'sed-seismic',
    },
  ],
  // Trusted signals for the upper-lane left column — external Trust-A feeds +
  // internal operational feeds, in the shared BoardSignal shape (mirrors OOA's
  // SignalsPanel: [icon] label · detail — [RAG badge] [provenance icon]).
  boardSignals: [
    { id: 'meteoswiss-heat', label: 'MeteoSwiss', detail: 'heat L3/5', iconKey: 'weather', scope: 'external', provenance: 'simulated', trustClass: 'Trust-A', statusLabel: 'ACTUAL', statusTone: 'watch' },
    { id: 'bag-resp', label: 'BAG/FOPH', detail: 'RSV rising', iconKey: 'pulse', scope: 'external', provenance: 'simulated', trustClass: 'Trust-A', statusLabel: 'ACTUAL', statusTone: 'watch' },
    { id: 'alertswiss-heat-test', label: 'Alertswiss/BABS', detail: 'quiet', iconKey: 'alert', scope: 'external', provenance: 'simulated', trustClass: 'Trust-A', statusLabel: '1 Test quar.', statusTone: 'signal' },
    { id: 'sed-seismic', label: 'SED-ETH', detail: 'seismic nominal', iconKey: 'seismic', scope: 'external', provenance: 'simulated', trustClass: 'Trust-A', statusLabel: 'OK', statusTone: 'ok' },
    { id: 'onco-rn-roster', label: 'Oncology RN roster', detail: '1 free', iconKey: 'people', scope: 'internal', provenance: 'simulated', statusLabel: 'THIN', statusTone: 'over' },
    { id: 'ed-arrivals', label: 'ED arrivals', detail: '+2 vs baseline', iconKey: 'heartpulse', scope: 'internal', provenance: 'simulated', statusLabel: 'WATCH', statusTone: 'watch' },
    { id: 'transfer-in-queue', label: 'Transfer-in queue', detail: '3 pending', iconKey: 'swap', scope: 'internal', provenance: 'simulated', statusLabel: 'WATCH', statusTone: 'watch' },
    { id: 'evs-turnaround', label: 'EVS turnaround', detail: '+2h', iconKey: 'clock', scope: 'internal', provenance: 'simulated', statusLabel: 'WATCH', statusTone: 'watch' },
  ],
  // Scenario queue (middle lane) — 6 shocks pressure-tested. recoIds resolve to
  // existing crisis recos (approval-gated); unknown ids fall back to defaultReco.
  scenarioQueue: [
    { id: 'SC-01', name: 'Oncology RN sick-call', trigger: 'binding constraint fails', impact: '−2 oncology beds', impactTone: 'over', likelihood: 'Likely', result: 'SIMULATE', recoId: 'crisis-readiness' },
    { id: 'SC-02', name: 'ED surge +4', trigger: 'winter respiratory peak', impact: '−4 beds beyond relief', impactTone: 'watch', likelihood: 'Possible', result: 'MODELLED', recoId: 'heatwave-surge' },
    { id: 'SC-03', name: 'Transfer-in surge', trigger: 'partner site divert', impact: '+3 inbound', impactTone: 'watch', likelihood: 'Possible', result: 'MODELLED', recoId: 'resp-virus-surge' },
    { id: 'SC-04', name: 'EVS turnaround slip', trigger: 'housekeeping backlog', impact: '+2h on 4 beds', impactTone: 'watch', likelihood: 'Watch', result: 'WATCH', recoId: 'crisis-readiness' },
    { id: 'SC-05', name: 'OR case overrun', trigger: 'oncology case runs long', impact: '2 post-op late', impactTone: 'ok', likelihood: 'Watch', result: 'HOLDS', recoId: 'crisis-readiness' },
    { id: 'SC-06', name: 'Combined shock', trigger: 'sick-call + surge stack', impact: '−6 beds worst-case', impactTone: 'muted', likelihood: 'Unlikely', result: 'STRESS-MAX', recoId: 'seismic-event' },
  ],
  // Resilience levers (lower lane) — curated priority order (SPOF first), matching the mockup rank 1..5.
  resilienceLevers: [
    { id: 'pre-stage-oncology', label: 'Pre-stage oncology backup', description: 'activate the agency-bank oncology RN sba held · standby-only, no cost unless SC-01 fires', bedsProtected: 2, icon: 'spof', owner: 'Staffing command', timing: 'likely', timingTone: 'over', window: 'pre-stage', spof: true, handoffTo: 'sba reserve', recoId: 'crisis-readiness' },
    { id: 'surge-buffer', label: 'Surge buffer', description: 'flex 4 beds in Surgery B (low-pressure) if ED spikes · cross-cover via sba SH-4405', bedsProtected: 4, icon: 'buffer', owner: 'Site flow', timing: 'possible', timingTone: 'watch', window: 'arm', recoId: 'crisis-readiness' },
    { id: 'discharge-accelerator', label: 'Discharge accelerator', description: "pull tomorrow's 4 dca-ready discharges forward if beds land late", bedsProtected: 4, impactLabel: '+4 beds', icon: 'discharge', owner: 'dca-agent', timing: 'ready', timingTone: 'ok', window: 'pull fwd', recoId: 'crisis-readiness' },
    { id: 'transfer-hold-gate', label: 'Transfer-hold gate', description: 'pause accepting regional transfers if shocks stack · auto-release once buffers arm', bedsProtected: 3, icon: 'gate', owner: 'Transfer desk', timing: 'possible', timingTone: 'watch', window: 'gate', recoId: 'crisis-readiness' },
    { id: 'site-command-escalation', label: 'Site command escalation', description: 'declare an internal capacity alert · exec bridge · combined worst-case only', bedsProtected: 0, impactLabel: 'last', icon: 'escalation', owner: 'Exec · START', timing: 'worst-case', timingTone: 'muted', window: 'bridge', handoffTo: 'exec', recoId: 'crisis-readiness' },
  ],
  resilienceSummary: { stressTested: 3, holdsUnder: 2, needsReserve: 1 },
  absorbed: { absorbed: 5, total: 6 },
  recoById,
  defaultReco,
};
